// Toast — one-shot ephemeral message at the bottom of the screen.
//
// Two ways to use:
//   1. <Toast show={bool} onDismiss={...}>message</Toast> — local-state driven.
//   2. <ToastHost /> mounted once near the app root + showToast('message') anywhere.
//
// Animation:
//   - .toast canon class (canon.css line 943) — fixed-position ink pill, pink
//     border, ≥44pt min-height via stumble-overrides.css.
//   - Entrance: canon `toast-in` keyframe (200ms ease-out).
//   - Exit: `toast-out` keyframe (180ms ease-in) defined in stumble-overrides.css.
//   - Auto-dismiss after `duration` ms (default 3000).
//
// Reduced motion: the stumble-overrides @media reduce block already strips
// animation from .toast. The component still renders; it just appears/disappears
// instantly.
//
// Bottom positioning: canon places .toast at bottom:90px; stumble-overrides
// lifts it by --safe-bottom so it sits clear of the URL bar + home indicator.

import { type ReactNode, useEffect, useState } from 'react'

export type ToastProps = {
  show: boolean
  children: ReactNode
  onDismiss?: () => void
  /** Milliseconds before auto-dismiss. Default 3000. Set to 0 to disable. */
  duration?: number
}

const EXIT_MS = 180

export function Toast({ show, children, onDismiss, duration = 3000 }: ToastProps) {
  const [exiting, setExiting] = useState(false)

  // Reset exit-state when toast (re-)opens so successive shows animate in fresh.
  useEffect(() => {
    if (show) setExiting(false)
  }, [show])

  // Auto-dismiss timer.
  useEffect(() => {
    if (!show || duration <= 0 || !onDismiss) return
    const dismissAt = window.setTimeout(() => {
      setExiting(true)
      // After exit animation, fire onDismiss to unmount.
      window.setTimeout(() => onDismiss(), EXIT_MS)
    }, duration)
    return () => window.clearTimeout(dismissAt)
  }, [show, duration, onDismiss])

  if (!show && !exiting) return null

  return (
    <output
      className={exiting ? 'toast toast-out' : 'toast'}
      data-testid="toast"
      // <output> is the right element for status messages — implicit role=status,
      // aria-live=polite is automatic so SR users hear the toast without focus stealing.
    >
      {children}
    </output>
  )
}

// ── Singleton API ────────────────────────────────────────────────────────────
// Optional alternative for one-shot toasts from anywhere (no prop-drilling).
// Usage: mount <ToastHost /> once in the app shell; call showToast('hi') anywhere.

type ToastEntry = { id: number; message: ReactNode; expiresAt: number }
type Listener = (entries: ToastEntry[]) => void

let entries: ToastEntry[] = []
let listeners: Listener[] = []
let nextId = 1

function emit() {
  for (const fn of listeners) fn(entries)
}

/**
 * Show a toast from anywhere. Returns a dismiss function.
 * Duration in ms; default 3000. Set to 0 to require manual dismiss.
 */
export function showToast(message: ReactNode, duration = 3000): () => void {
  const id = nextId++
  const entry: ToastEntry = {
    id,
    message,
    expiresAt: duration > 0 ? Date.now() + duration : Number.POSITIVE_INFINITY,
  }
  entries = [...entries, entry]
  emit()
  if (duration > 0) {
    window.setTimeout(() => dismiss(id), duration)
  }
  return () => dismiss(id)
}

function dismiss(id: number) {
  if (!entries.some((e) => e.id === id)) return
  entries = entries.filter((e) => e.id !== id)
  emit()
}

/**
 * Mount once near the app root. Renders all active toasts in a column above
 * the bottom-nav. Most apps will have at most 1 toast at a time, but rapid-fire
 * actions could stack briefly.
 */
export function ToastHost() {
  const [state, setState] = useState<ToastEntry[]>(entries)
  useEffect(() => {
    const fn: Listener = (next) => setState(next)
    listeners.push(fn)
    return () => {
      listeners = listeners.filter((l) => l !== fn)
    }
  }, [])
  if (state.length === 0) return null
  return (
    <>
      {state.map((e) => (
        <output className="toast" key={e.id} data-testid="toast">
          {e.message}
        </output>
      ))}
    </>
  )
}
